import { PRODUCT_TYPE_TICKET } from 'constants/app'
import { useMemo } from 'react'
import { GetCartItemPresenter } from 'store/api-slice/hooks'

export type FamilySummary = {
    totalPrice: number
    totalDays: number
}

export const useFamilyPackage = (data: GetCartItemPresenter[]) => {
    // condensed info about bundle items' prices and days
    const summary = useMemo(() => {
        // collect bundle items' prices and days info, one by one
        const totalPricesByBundles: FamilySummary[] = data.map(
            ({ unitPrice, itemsBundle }) => {
                // in one bundle all tickets are the same weekday or weekend type
                const sampleTicketFromBundle = itemsBundle?.find(
                    ({ productType }) => productType === PRODUCT_TYPE_TICKET
                )

                if (!sampleTicketFromBundle)
                    return {
                        totalPrice: 0,
                        totalDays: 0,
                    }

                const standarizedUnitPrice = unitPrice || 0

                return {
                    totalPrice: standarizedUnitPrice,
                    totalDays: 1,
                }
            }
        )
        // reduce data for summary
        const reducedPrices: FamilySummary = totalPricesByBundles.reduce(
            (accumulator, currentValue) => {
                const { totalPrice: accTotalPrice, totalDays: accTotalDays } =
                    accumulator
                const { totalPrice: currTotalPrice, totalDays: currTotalDays } =
                    currentValue

                return {
                    totalPrice: accTotalPrice + currTotalPrice,
                    totalDays: accTotalDays + currTotalDays,
                }
            },
            {
                totalPrice: 0,
                totalDays: 0,
            }
        )

        return reducedPrices
    }, [data])

    // condensed info about items included in bundle
    const items = useMemo(() => {
        if (!data.length) return []

        // all bundles have the same items, so we need data only from one
        const sampleBundle = data[0]
        const sampleBundleItems = sampleBundle.itemsBundle || []

        // extra reduce the same items in bundle, increase only quantity
        const reducedItems: GetCartItemPresenter[] = sampleBundleItems.reduce(
            (
                accumulator: GetCartItemPresenter[],
                currentValue: GetCartItemPresenter
            ) => {
                const { productId: currProductId } = currentValue

                const similarAccItemIndex = accumulator.findIndex(
                    ({ productId: accProductId }) =>
                        accProductId === currProductId
                )

                if (similarAccItemIndex !== -1) {
                    const similarItem = accumulator[similarAccItemIndex]
                    const similarItemQuantity = similarItem.quantity || 0

                    accumulator[similarAccItemIndex] = {
                        ...accumulator[similarAccItemIndex],
                        quantity: similarItemQuantity + 1,
                    }
                } else accumulator.push(currentValue)

                return accumulator
            },
            []
        )

        // put vouchers at the end of the list
        const sortedItems = reducedItems.sort((itemA, itemB) => {
            const weightA = itemA.name || ''
            const weightB = itemB.name || ''

            if (weightA < weightB) return -1
            if (weightA > weightB) return 1
            return 0
        })

        return sortedItems
    }, [data])

    return {
        summary,
        items,
    }
}
